import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Divider,
  styled,
  alpha,
  Tooltip,
  Snackbar,
  Alert,
  Rating,
  Chip,
  LinearProgress,
  Button,
  Container,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  InputAdornment,
  MenuItem,
  Grid
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import WarningIcon from '@mui/icons-material/Warning';
import NoteIcon from '@mui/icons-material/Note';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border, shadow } from '../../utils/darkModeAdapter';
import { activityTracker } from '../../utils/activityTracker';

// axios設定
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// APIヘルパー関数
const addAnswer = async (answerData) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // リクエストデータの形式をInterviewAnswerCreateスキーマに合わせる
  const requestData = {
    answers: [{
      question_id: answerData.question_id,
      check_sheet_id: answerData.check_sheet_id,
      rating: answerData.rating ? parseInt(answerData.rating, 10) : null,
      notes: answerData.notes || answerData.memo || null,  // 'memo'または'notes'をサポート
      user_id: parseInt(localStorage.getItem('userId') || '0')
    }]
  };
  
  // バルク保存用のエンドポイントを使用
  return axios.post(`${API_URL}/interview_assistant/bulk-answers`, requestData, { 
    headers,
    withCredentials: true,
    timeout: 15000 // 15秒タイムアウト
  });
};

// レーティングレベルに合わせた色を返す関数（1-5の数値に対応）
const getRatingColor = (rating) => {
  const numRating = typeof rating === 'string' ? parseInt(rating, 10) : rating;
  if (numRating >= 4.5) return '#4caf50';
  if (numRating >= 3.5) return '#8bc34a';
  if (numRating >= 2.5) return '#ffc107';
  if (numRating >= 1.5) return '#ff9800';
  if (numRating >= 1) return '#f44336';
  return '#9e9e9e';
};

/**
 * 面接チェックシート (評価項目と想定質問でまとめた表)
 */
const InterviewCheckSheet = forwardRef(({
  evaluationCategories,
  setEvaluationCategories,
  checkSheetId,
  onSaveAnswers = null,
  isReadOnly = false,
  initialApplicantInfo = {},
  initialInterviewerInfo = {},
  isInterviewCompleted = false,
  // 項目追加用props
  categoryNameInput = '',
  setCategoryNameInput = null,
  handleAddCategory = null,
  selectedCategory = 0,
  setSelectedCategory = null,
  itemTextInput = '',
  setItemTextInput = null,
  handleAddItem = null,
  showAddSection = false,
  // 親/子アカウント回答切り替え用
  viewingAnswers = null, // 子アカウントの回答データ（配列）
  viewingAccountLabel = null // 表示中のアカウントラベル
}, ref) => {
  const theme = useTheme();

  // 評価値を管理するstate
  const [ratings, setRatings] = useState(() => {
    // ローカルストレージから復元
    const savedRatings = localStorage.getItem(`checksheet_ratings_${checkSheetId}`);
    return savedRatings ? JSON.parse(savedRatings) : {};
  });
  
  // メモを管理するstate
  const [memos, setMemos] = useState(() => {
    // ローカルストレージから復元
    const savedMemos = localStorage.getItem(`checksheet_memos_${checkSheetId}`);
    return savedMemos ? JSON.parse(savedMemos) : {};
  });
  
  // 質問IDを管理するstate
  const [questionIds, setQuestionIds] = useState(() => {
    // ローカルストレージから復元
    const savedQuestionIds = localStorage.getItem(`checksheet_questionIds_${checkSheetId}`);
    return savedQuestionIds ? JSON.parse(savedQuestionIds) : {};
  });

  // checkSheetIdが変わったらローカルストレージから再取得
  // ただし、evaluationCategoriesからの復元と競合しないよう、
  // evaluationCategoriesが存在しない場合のみ実行
  useEffect(() => {
    if (!checkSheetId) return;
    
    // evaluationCategoriesが存在する場合は、そちらから復元するため
    // ローカルストレージからの復元はスキップ
    if (evaluationCategories && evaluationCategories.length > 0) {
      return;
    }

    const savedRatings = localStorage.getItem(`checksheet_ratings_${checkSheetId}`);
    setRatings(savedRatings ? JSON.parse(savedRatings) : {});
    const savedMemos = localStorage.getItem(`checksheet_memos_${checkSheetId}`);
    setMemos(savedMemos ? JSON.parse(savedMemos) : {});
    const savedQuestionIds = localStorage.getItem(`checksheet_questionIds_${checkSheetId}`);
    setQuestionIds(savedQuestionIds ? JSON.parse(savedQuestionIds) : {});
  }, [checkSheetId, evaluationCategories]);
  
  // 最終保存時間の管理
  const [lastSaved, setLastSaved] = useState(null);

  // 保存状態の管理
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'

  // 自動保存のタイマーref
  const autoSaveTimerRef = useRef(null);

  // 自動保存関数
  const autoSaveAnswers = useCallback(async () => {
    if (!checkSheetId || isReadOnly) return;

    try {
      setSaveStatus('saving');

      // 保存するデータを収集
      const answersToSave = [];

      evaluationCategories.forEach(category => {
        category.items.forEach(item => {
          // 評価またはメモが入力されている質問のみ保存
          if (ratings[item.id] || memos[item.id]) {
            const questionId = item.questionId || questionIds[item.id];

            if (questionId) {
              answersToSave.push({
                question_id: typeof questionId === 'string' ? parseInt(questionId, 10) : questionId,
                rating: ratings[item.id] ? parseInt(ratings[item.id], 10) : null,
                notes: memos[item.id] || null
              });
            }
          }
        });
      });

      // 保存するデータがある場合のみAPIを呼び出す
      if (answersToSave.length > 0) {
        const token = localStorage.getItem('token');
        const requestData = {
          check_sheet_id: checkSheetId,
          answers: answersToSave
        };

        await axios.post(
          `${API_URL}/interview_assistant/bulk-answers`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            },
            withCredentials: true,
            timeout: 15000
          }
        );

        setSaveStatus('saved');
        setLastSaved(new Date());
      } else {
        setSaveStatus('saved');
      }
    } catch (error) {
      console.error('自動保存に失敗しました:', error);
      setSaveStatus('error');

      // エラーの場合は5秒後に'unsaved'状態に戻す
      setTimeout(() => {
        setSaveStatus('unsaved');
      }, 5000);
    }
  }, [checkSheetId, isReadOnly, ratings, memos, questionIds, evaluationCategories]);

  // ratingsとmemosの変更を監視して自動保存
  useEffect(() => {
    if (!checkSheetId || isReadOnly) return;

    // 変更があったらunsavedに設定
    setSaveStatus('unsaved');

    // 既存のタイマーをクリア
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // 3秒後に自動保存を実行
    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveAnswers();
    }, 3000);

    // クリーンアップ
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [ratings, memos, checkSheetId, isReadOnly, autoSaveAnswers]);

  // Feedback states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 削除: candidateInfo と interviewerInfo の state
  const [errors, setErrors] = useState({});
  
  // 前回のevaluationCategoriesを記憶するref
  const prevEvaluationCategoriesRef = useRef();
  const prevCheckSheetIdRef = useRef();
  const hasValidatedRef = useRef(false);

  // ratingsが変更されたらローカルストレージに保存
  useEffect(() => {
    if (!checkSheetId) return;
    localStorage.setItem(`checksheet_ratings_${checkSheetId}`, JSON.stringify(ratings));
  }, [ratings, checkSheetId]);

  // memosが変更されたらローカルストレージに保存
  useEffect(() => {
    if (!checkSheetId) return;
    localStorage.setItem(`checksheet_memos_${checkSheetId}`, JSON.stringify(memos));
  }, [memos, checkSheetId]);

  // questionIdsが変更されたらローカルストレージに保存
  useEffect(() => {
    if (!checkSheetId) return;
    localStorage.setItem(`checksheet_questionIds_${checkSheetId}`, JSON.stringify(questionIds));
  }, [questionIds, checkSheetId]);
  
  // evaluationCategoriesからrating/notesデータを復元（過去のチェックシート選択時）
  useEffect(() => {
    if (!evaluationCategories || !checkSheetId) return;
    
    // checkSheetIdが変わった時のみ復元処理を実行
    const currentCheckSheetId = checkSheetId;
    const prevCheckSheetId = prevCheckSheetIdRef.current;
    
    if (currentCheckSheetId === prevCheckSheetId) {
      // 同じチェックシートIDの場合は処理をスキップ
      return;
    }
    
    // rating/notesが含まれているかを詳細チェック
    let totalItemsWithRating = 0;
    let totalItemsWithNotes = 0;
    evaluationCategories.forEach(cat => {
      cat.items?.forEach(item => {
        if (item.rating) totalItemsWithRating++;
        if (item.notes) totalItemsWithNotes++;
      });
    });

    const newRatings = {};
    const newMemos = {};
    let hasRatingData = false;
    let hasMemoData = false;
    
    evaluationCategories.forEach((cat) => {
      cat.items?.forEach((item) => {
        // evaluationCategoriesからデータを復元
        if (item.rating !== undefined && item.rating !== '' && item.rating !== null) {
          const ratingValue = String(item.rating); // 文字列として保存
          newRatings[item.id] = ratingValue;
          hasRatingData = true;
        }
        
        if (item.notes !== undefined && item.notes !== '' && item.notes !== null) {
          newMemos[item.id] = item.notes;
          hasMemoData = true;
        }
      });
    });
    
    setRatings(newRatings);
    setMemos(newMemos);
    
    // 現在のcheckSheetIdを記憶
    prevCheckSheetIdRef.current = currentCheckSheetId;
  }, [evaluationCategories, checkSheetId]); // 依存配列は変更なし
  
  // データ復元後の検証・修正用useEffect（遅延実行）
  useEffect(() => {
    if (!evaluationCategories?.length || !checkSheetId) {
      hasValidatedRef.current = false;
      return;
    }
    
    // checkSheetIdが変わった場合は検証フラグをリセット
    if (prevCheckSheetIdRef.current !== checkSheetId) {
      hasValidatedRef.current = false;
    }
    
    // 既に検証済みの場合はスキップ
    if (hasValidatedRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      let needsRatingUpdate = false;
      let needsMemosUpdate = false;
      const updatedRatings = {};
      const updatedMemos = {};
      
      evaluationCategories.forEach(cat => {
        cat.items?.forEach(item => {
          // ratingデータの検証・修正
          if (item.rating && item.rating !== '' && item.rating !== null) {
            updatedRatings[item.id] = String(item.rating);
            needsRatingUpdate = true;
          }
          
          // notesデータの検証・修正
          if (item.notes && item.notes !== '' && item.notes !== null) {
            updatedMemos[item.id] = item.notes;
            needsMemosUpdate = true;
          }
        });
      });
      
      if (needsRatingUpdate) {
        setRatings(prev => ({ ...prev, ...updatedRatings }));
      }

      if (needsMemosUpdate) {
        setMemos(prev => ({ ...prev, ...updatedMemos }));
      }

      hasValidatedRef.current = true;
    }, 200); // 200ms遅延
    
    return () => clearTimeout(timer);
  }, [evaluationCategories, checkSheetId]); // ratings, memosを依存配列から削除

  // 評価カテゴリデータが更新されたら、質問IDを初期化
  useEffect(() => {
    if (!checkSheetId || !evaluationCategories?.length) return;
    
    // rating/notesデータが含まれている場合は質問IDマッピングをスキップ
    // （既に復元済みのデータを保護するため）
    const hasRatingData = evaluationCategories.some(cat => 
      cat.items?.some(item => item.rating !== undefined && item.rating !== null && item.rating !== '')
    );
    
    if (hasRatingData) {
      return;
    }
    
    // 現在のID情報をクローン
    const updatedQuestionIds = { ...questionIds };
    let hasUpdates = false;
    
    // 各カテゴリ・項目をループし、有効なquestionIdをマップに追加
    evaluationCategories.forEach(category => {
      category.items.forEach(item => {
        // 質問IDの優先順位: item.questionId > questionIds[item.id]
        const existingQuestionId = item.questionId || questionIds[item.id];
        
        if (existingQuestionId && (!updatedQuestionIds[item.id] || updatedQuestionIds[item.id] !== existingQuestionId)) {
          // 数値に変換を試みる（文字列の場合）
          const processedId = typeof existingQuestionId === 'string' && !isNaN(existingQuestionId)
            ? parseInt(existingQuestionId, 10)
            : existingQuestionId;
          
          // 有効なIDを保存（数値でも文字列でも）
          if (processedId !== null && processedId !== undefined) {
            updatedQuestionIds[item.id] = processedId;
            hasUpdates = true;
          }
        }
      });
    });
    
    // 更新があった場合のみsetQuestionIdsを呼び出す
    if (hasUpdates) {
      setQuestionIds(updatedQuestionIds);
      // ローカルストレージに保存
      localStorage.setItem(`checksheet_questionIds_${checkSheetId}`, JSON.stringify(updatedQuestionIds));
    }
  }, [evaluationCategories, checkSheetId, questionIds]);
  
  // 評価の選択を処理する関数
  const handleRatingChange = (itemId, value) => {
    setRatings(prev => ({
      ...prev,
      [itemId]: value
    }));
  };
  
  // メモの変更を処理する関数
  const handleMemoChange = (itemId, value) => {
    setMemos(prev => ({
      ...prev,
      [itemId]: value
    }));
  };
  
  // Close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({...prev, open: false}));
  };
  
  // 成功メッセージを表示する関数（外部から呼び出し可能）
  const showSuccess = (message) => {
    setSnackbar({
      open: true,
      message: message || '回答が保存されました',
      severity: 'success'
    });
  };
  
  // エラーメッセージを表示する関数（外部から呼び出し可能）
  const showError = (message) => {
    setSnackbar({
      open: true,
      message: message || 'エラーが発生しました',
      severity: 'error'
    });
  };

  // 質問を削除する関数
  const handleDeleteQuestion = async (categoryIndex, itemIndex) => {
    if (!window.confirm('この質問を削除してもよろしいですか？\n削除した質問と関連する回答データは復元できません。')) {
      return;
    }

    try {
      const updatedCategories = [...evaluationCategories];
      const deletedItem = updatedCategories[categoryIndex].items[itemIndex];

      // 質問IDの取得
      const questionId = deletedItem.questionId || questionIds[deletedItem.id];

      // 質問IDが存在する場合、APIを呼び出してデータベースから削除
      if (questionId) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(
            `${API_URL}/interview_assistant/questions/${questionId}`,
            {
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` })
              },
              withCredentials: true
            }
          );

          // 削除が成功した場合のみUIを更新
          updatedCategories[categoryIndex].items.splice(itemIndex, 1);
          setEvaluationCategories(updatedCategories);

          // 関連するメモと評価も削除
          const itemId = deletedItem.id;
          const updatedRatings = { ...ratings };
          delete updatedRatings[itemId];
          setRatings(updatedRatings);

          const updatedMemos = { ...memos };
          delete updatedMemos[itemId];
          setMemos(updatedMemos);

          const updatedQuestionIds = { ...questionIds };
          delete updatedQuestionIds[itemId];
          setQuestionIds(updatedQuestionIds);

          // ローカルストレージの更新
          if (checkSheetId) {
            localStorage.setItem(`checksheet_ratings_${checkSheetId}`, JSON.stringify(updatedRatings));
            localStorage.setItem(`checksheet_memos_${checkSheetId}`, JSON.stringify(updatedMemos));
            localStorage.setItem(`checksheet_questionIds_${checkSheetId}`, JSON.stringify(updatedQuestionIds));
          }

          // 成功メッセージを表示
          setSnackbar({
            open: true,
            message: '質問を削除しました',
            severity: 'success'
          });
        } catch (error) {
          console.error('質問の削除に失敗しました:', error);
          setSnackbar({
            open: true,
            message: error.response?.data?.detail || '質問の削除に失敗しました',
            severity: 'error'
          });
        }
      } else {
        // 質問IDがない場合（新規追加された質問など）は、UIからのみ削除
        updatedCategories[categoryIndex].items.splice(itemIndex, 1);
        setEvaluationCategories(updatedCategories);

        setSnackbar({
          open: true,
          message: '質問を削除しました',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('質問の削除中にエラーが発生しました:', error);
      setSnackbar({
        open: true,
        message: '質問の削除中にエラーが発生しました',
        severity: 'error'
      });
    }
  };

  // 評価項目（カテゴリ）削除ダイアログを開く
  const handleDeleteCategory = (categoryIndex) => {
    const category = evaluationCategories[categoryIndex];
    setDeleteDialog({
      open: true,
      categoryIndex,
      categoryName: category.name,
      itemCount: category.items.length
    });
  };

  // 評価項目（カテゴリ）削除ダイアログをキャンセル
  const handleCancelDelete = () => {
    setDeleteDialog({
      open: false,
      categoryIndex: null,
      categoryName: '',
      itemCount: 0
    });
  };

  // 評価項目（カテゴリ）を実際に削除する関数
  const handleConfirmDelete = async () => {
    const { categoryIndex } = deleteDialog;
    const category = evaluationCategories[categoryIndex];

    // ダイアログを閉じる
    handleCancelDelete();

    try {
      // カテゴリ内のすべての質問をDBから削除
      for (const item of category.items) {
        const questionId = item.questionId || questionIds[item.id];
        if (questionId) {
          try {
            const token = localStorage.getItem('token');
            await axios.delete(
              `${API_URL}/interview_assistant/questions/${questionId}`,
              {
                headers: {
                  'Content-Type': 'application/json',
                  ...(token && { Authorization: `Bearer ${token}` })
                },
                withCredentials: true
              }
            );
          } catch (error) {
            console.error(`質問ID ${questionId} の削除に失敗:`, error);
            // 個別の質問削除が失敗しても続行
          }
        }
      }

      // UIからカテゴリを削除
      const updatedCategories = evaluationCategories.filter((_, idx) => idx !== categoryIndex);
      setEvaluationCategories(updatedCategories);

      // 関連する評価・メモ・質問IDを削除
      const updatedRatings = { ...ratings };
      const updatedMemos = { ...memos };
      const updatedQuestionIds = { ...questionIds };

      category.items.forEach(item => {
        delete updatedRatings[item.id];
        delete updatedMemos[item.id];
        delete updatedQuestionIds[item.id];
      });

      setRatings(updatedRatings);
      setMemos(updatedMemos);
      setQuestionIds(updatedQuestionIds);

      // ローカルストレージを更新
      if (checkSheetId) {
        localStorage.setItem(`checksheet_ratings_${checkSheetId}`, JSON.stringify(updatedRatings));
        localStorage.setItem(`checksheet_memos_${checkSheetId}`, JSON.stringify(updatedMemos));
        localStorage.setItem(`checksheet_questionIds_${checkSheetId}`, JSON.stringify(updatedQuestionIds));
      }

      // 成功メッセージ
      setSnackbar({
        open: true,
        message: `評価項目「${category.name}」を削除しました`,
        severity: 'success'
      });

    } catch (error) {
      console.error('評価項目の削除に失敗:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || '評価項目の削除に失敗しました',
        severity: 'error'
      });
    }
  };

  // 親コンポーネントから呼び出せる評価データの取得・適用メソッド
  useImperativeHandle(ref, () => ({
    getAndApplyEvaluationData: () => {
      // 評価データを収集
      const answers = [];
      const processedQuestionIds = new Set(); // 重複チェック用
      
      evaluationCategories.forEach(category => {
        category.items.forEach(item => {
          if (ratings[item.id]) {
            // 質問IDを取得（優先順位: item.questionId > questionIds[item.id]）
            const questionId = item.questionId || questionIds[item.id];
            
            // 質問IDが数値の場合はそのまま使用、文字列の場合は数値に変換を試みる
            const processedQuestionId = typeof questionId === 'string' && !isNaN(questionId) 
              ? parseInt(questionId, 10) 
              : questionId;
            
            if (processedQuestionId && !processedQuestionIds.has(processedQuestionId)) {
              answers.push({
                question_id: processedQuestionId,
                rating: parseInt(ratings[item.id], 10),
                notes: memos[item.id] || ''
              });
              
              processedQuestionIds.add(processedQuestionId);
            }
          }
        });
      });
      
      return {
        answers,
        questionIds,
        ratings,
        memos
      };
    },
    setRatings,
    setMemos
  }));

  const handleSaveAnswers = async () => {
    const endTracking = await activityTracker.trackFeatureUse('interview_check_sheet_update', {
      checkSheetId,
      ratings,
      memos
    });

    try {
      if (onSaveAnswers) {
        const evaluationData = ref.current.getAndApplyEvaluationData();
        onSaveAnswers(evaluationData.answers, evaluationData.ratings, evaluationData.memos, evaluationData.questionIds);
      }
      setLastSaved(new Date());
      setSnackbar({
        open: true,
        message: '回答を保存しました',
        severity: 'success'
      });
    } finally {
      endTracking();
    }
  };

  // カテゴリごとの平均評価を計算
  const calculateCategoryAverage = (category) => {
    const itemsWithRatings = category.items.filter(item => ratings[item.id]);
    if (itemsWithRatings.length === 0) return 0;
    const sum = itemsWithRatings.reduce((acc, item) => {
      const rating = parseInt(ratings[item.id], 10);
      return acc + (isNaN(rating) ? 0 : rating);
    }, 0);
    return sum / itemsWithRatings.length;
  };

  // 各カテゴリの展開状態を管理
  const [expandedCategories, setExpandedCategories] = useState(() => {
    // 初期状態：全て展開
    return evaluationCategories.reduce((acc, _, index) => {
      acc[index] = true;
      return acc;
    }, {});
  });

  const handleCategoryToggle = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  // メモの展開状態を管理（item.id -> boolean）
  const [expandedMemos, setExpandedMemos] = useState({});

  const toggleMemoExpand = (itemId) => {
    setExpandedMemos(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // 削除確認ダイアログのstate
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    categoryIndex: null,
    categoryName: '',
    itemCount: 0
  });

  return (
    <Container maxWidth="lg">      
      <Paper
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          position: 'relative',
          opacity: isReadOnly ? 0.95 : 1
        }}
      >
        {/* 読み取り専用モードのオーバーレイバッジ */}
        {isReadOnly && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              px: 2,
              py: 0.5,
              borderRadius: 10,
              boxShadow: 2
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
              評価完了
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 24, mr: 1.5, color: 'primary.main' }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                面接チェックシート
              </Typography>
            </Box>
            {/* 表示中のアカウントラベル */}
            {viewingAccountLabel && (
              <Chip
                label={`${viewingAccountLabel}の回答`}
                size="small"
                color="secondary"
                sx={{
                  fontWeight: 600,
                  px: 1
                }}
              />
            )}
          </Box>

          {/* 自動保存ステータス表示 */}
          {!isReadOnly && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {saveStatus === 'saving' && (
                <>
                  <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    保存中...
                  </Typography>
                </>
              )}
              {saveStatus === 'saved' && lastSaved && (
                <>
                  <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" sx={{ color: 'success.main' }}>
                    保存済み
                  </Typography>
                </>
              )}
              {saveStatus === 'unsaved' && (
                <>
                  <SaveIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    未保存の変更
                  </Typography>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  <Typography variant="caption" sx={{ color: 'error.main' }}>
                    保存エラー
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        
        {/* 評価の指示を表示 */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            bgcolor: alpha('#2196f3', 0.05),
            border: '1px solid',
            borderColor: alpha('#2196f3', 0.2)
          }}
        >
          {isReadOnly ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ mr: 1.5, color: 'success.main' }} />
              <Typography variant="body2" color="text.secondary">
                面接評価が完了しました。このチェックシートは編集できません。
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ '& .MuiListItem-root': { py: 0.25, px: 0 } }}>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <StarIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                </ListItemIcon>
                <ListItemText
                  primary="各質問に★評価（1-5）"
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <NoteIcon sx={{ fontSize: 18, color: textColors(theme).secondary }} />
                </ListItemIcon>
                <ListItemText
                  primary="メモは任意"
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AutorenewIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                </ListItemIcon>
                <ListItemText
                  primary="自動保存（変更後3秒）"
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            </List>
          )}

          {/* 評価項目・質問の追加セクション（コンパクト版） */}
          {showAddSection && !isReadOnly && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                border: '1px dashed',
                borderColor: alpha('#2196f3', 0.3),
                bgcolor: alpha('#2196f3', 0.02)
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                {/* 評価項目の追加 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="新しい評価項目名"
                      value={categoryNameInput}
                      onChange={(e) => setCategoryNameInput && setCategoryNameInput(e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: bg(theme).card,
                          borderRadius: 1,
                          '& input': { py: 0.75, fontSize: '0.8rem' }
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddCategory}
                      disabled={!categoryNameInput?.trim()}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        background: 'linear-gradient(90deg, #2196F3, #21CBF3)',
                        textTransform: 'none',
                        fontSize: '0.75rem'
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16, mr: 0.25 }} />
                      項目追加
                    </Button>
                  </Box>
                </Grid>

                {/* 想定質問の追加 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {evaluationCategories.length > 0 && (
                      <TextField
                        select
                        size="small"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory && setSelectedCategory(e.target.value)}
                        sx={{
                          minWidth: 100,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: bg(theme).card,
                            borderRadius: 1
                          },
                          '& .MuiSelect-select': { py: 0.75, fontSize: '0.8rem' }
                        }}
                      >
                        {evaluationCategories.map((cat, idx) => (
                          <MenuItem key={idx} value={idx} sx={{ fontSize: '0.8rem' }}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="質問内容"
                      value={itemTextInput}
                      onChange={(e) => setItemTextInput && setItemTextInput(e.target.value)}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: bg(theme).card,
                          borderRadius: 1,
                          '& input': { py: 0.75, fontSize: '0.8rem' }
                        }
                      }}
                      disabled={evaluationCategories.length === 0}
                    />
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={handleAddItem}
                      disabled={evaluationCategories.length === 0 || !itemTextInput?.trim()}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        background: 'linear-gradient(90deg, #9c27b0, #e040fb)',
                        textTransform: 'none',
                        fontSize: '0.75rem'
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16, mr: 0.25 }} />
                      質問追加
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        {/* カードアコーディオン形式の評価項目 */}
        <Box sx={{ mb: 3 }}>
          {evaluationCategories.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: bg(theme).subtle,
                borderRadius: 2
              }}
            >
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                まだ評価項目と想定質問が登録されていません
              </Typography>
            </Paper>
          ) : (
            evaluationCategories.map((category, categoryIndex) => {
              const avgRating = calculateCategoryAverage(category);
              const isExpanded = expandedCategories[categoryIndex];

              return (
                <Card
                  key={`category-${categoryIndex}`}
                  component={motion.div}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.2,
                    delay: categoryIndex * 0.05
                  }}
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    boxShadow: shadow(theme).sm,
                    border: `1px solid ${border(theme).light}`,
                    overflow: 'hidden',
                    bgcolor: bg(theme).card
                  }}
                >
                  {/* カテゴリヘッダー（コンパクト） */}
                  <Box
                    onClick={() => handleCategoryToggle(categoryIndex)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      cursor: 'pointer',
                      bgcolor: isExpanded ? alpha('#2196f3', 0.04) : bg(theme).subtle,
                      borderBottom: isExpanded ? `1px solid ${border(theme).light}` : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      '&:hover': {
                        bgcolor: alpha('#2196f3', 0.06)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AssignmentIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                        {category.name}
                      </Typography>
                      <Chip
                        label={`${category.items.length}問`}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', bgcolor: border(theme).light }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {avgRating > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Rating
                            value={avgRating}
                            precision={0.1}
                            readOnly
                            size="medium"
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: getRatingColor(avgRating)
                              },
                              '& .MuiRating-iconEmpty': {
                                color: '#D1D5DB',
                                opacity: 1,
                              },
                              '& .MuiRating-icon': { fontSize: 20 }
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: getRatingColor(avgRating)
                            }}
                          >
                            {avgRating.toFixed(1)}
                          </Typography>
                        </Box>
                      )}

                      {/* 削除ボタン（user_addedのみ表示） */}
                      {!isReadOnly && category.source === 'user_added' && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(categoryIndex);
                          }}
                          size="small"
                          sx={{
                            p: 0.5,
                            color: textColors(theme).disabled,
                            '&:hover': { color: '#ef4444' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}

                      <ExpandMoreIcon
                        sx={{
                          fontSize: 20,
                          color: textColors(theme).muted,
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease'
                        }}
                      />
                    </Box>
                  </Box>

                  {/* 質問リスト（テーブル形式） */}
                  <AnimatePresence>
                    {isExpanded && (
                      <Box
                        component={motion.div}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        sx={{ overflow: 'hidden' }}
                      >
                        {category.items.length > 0 ? (
                          <TableContainer>
                            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1, px: 1.5 } }}>
                              <TableHead>
                                <TableRow sx={{ bgcolor: alpha('#2196f3', 0.04) }}>
                                  <TableCell sx={{ fontWeight: 600, color: textColors(theme).muted, width: '50%' }}>質問</TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: textColors(theme).muted, width: '140px', textAlign: 'center' }}>評価</TableCell>
                                  <TableCell sx={{ fontWeight: 600, color: textColors(theme).muted, width: '40%' }}>メモ</TableCell>
                                  {!isReadOnly && <TableCell sx={{ width: '40px' }} />}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {category.items.map((item, itemIndex) => {
                                  // viewingAnswersが提供されている場合は子アカウントの回答を表示
                                  // viewingAnswersは配列形式で {question_id, rating, notes} を持つ
                                  let currentRating = 0;
                                  let currentMemo = '';

                                  if (viewingAnswers) {
                                    // 子アカウントの回答から該当する質問の回答を検索
                                    const questionId = item.questionId || questionIds[item.id];
                                    const childAnswer = viewingAnswers.find(
                                      ans => ans.question_id === questionId || ans.question_id === parseInt(questionId, 10)
                                    );
                                    if (childAnswer) {
                                      currentRating = parseInt(childAnswer.rating || 0, 10);
                                      currentMemo = childAnswer.notes || '';
                                    }
                                  } else {
                                    // 親アカウント（デフォルト）の回答を表示
                                    currentRating = parseInt(ratings[item.id] || item.rating || 0, 10);
                                    currentMemo = memos[item.id] || item.notes || '';
                                  }

                                  const isMemoExpanded = expandedMemos[item.id];
                                  // viewingAnswersが提供されている場合は読み取り専用モードにする
                                  const isViewingChildAccount = !!viewingAnswers;

                                  return (
                                    <React.Fragment key={`item-${item.id}`}>
                                      <TableRow
                                        sx={{
                                          '&:hover': { bgcolor: alpha('#2196f3', 0.02) },
                                          borderBottom: isMemoExpanded ? 'none' : undefined
                                        }}
                                      >
                                        {/* 質問テキスト */}
                                        <TableCell>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: textColors(theme).primary,
                                              lineHeight: 1.4,
                                              fontSize: '0.875rem'
                                            }}
                                          >
                                            {item.text}
                                          </Typography>
                                        </TableCell>

                                        {/* 星評価 */}
                                        <TableCell sx={{ textAlign: 'center' }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            <Rating
                                              value={currentRating}
                                              onChange={(_, newValue) => !(isReadOnly || isViewingChildAccount) && handleRatingChange(item.id, newValue ? newValue.toString() : '0')}
                                              disabled={isReadOnly || isViewingChildAccount}
                                              size="medium"
                                              sx={{
                                                '& .MuiRating-iconFilled': {
                                                  color: getRatingColor(currentRating)
                                                },
                                                '& .MuiRating-iconEmpty': {
                                                  color: '#D1D5DB',
                                                  opacity: 1,
                                                },
                                                '&.Mui-disabled': {
                                                  opacity: 1,
                                                },
                                              }}
                                            />
                                          </Box>
                                        </TableCell>

                                        {/* メモ欄（インライン/折りたたみ） */}
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {isMemoExpanded && !isViewingChildAccount ? (
                                              <TextField
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                placeholder="メモ..."
                                                value={currentMemo}
                                                onChange={(e) => !(isReadOnly || isViewingChildAccount) && handleMemoChange(item.id, e.target.value)}
                                                disabled={isReadOnly || isViewingChildAccount}
                                                autoFocus
                                                multiline
                                                maxRows={3}
                                                sx={{
                                                  '& .MuiInputBase-root': {
                                                    fontSize: '0.8rem',
                                                    py: 0.5
                                                  }
                                                }}
                                                slotProps={{
                                                  input: {
                                                    endAdornment: (
                                                      <InputAdornment position="end">
                                                        <IconButton
                                                          size="small"
                                                          onClick={() => toggleMemoExpand(item.id)}
                                                          sx={{ p: 0.25 }}
                                                        >
                                                          <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                      </InputAdornment>
                                                    )
                                                  }
                                                }}
                                              />
                                            ) : (
                                              <>
                                                <Typography
                                                  variant="body2"
                                                  onClick={() => !(isReadOnly || isViewingChildAccount) && toggleMemoExpand(item.id)}
                                                  sx={{
                                                    color: currentMemo ? textColors(theme).secondary : textColors(theme).disabled,
                                                    fontSize: '0.8rem',
                                                    cursor: (isReadOnly || isViewingChildAccount) ? 'default' : 'pointer',
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    '&:hover': !(isReadOnly || isViewingChildAccount) ? { color: '#2196f3' } : {}
                                                  }}
                                                >
                                                  {currentMemo || (isViewingChildAccount ? '(メモなし)' : 'メモを追加...')}
                                                </Typography>
                                                {!(isReadOnly || isViewingChildAccount) && (
                                                  <IconButton
                                                    size="small"
                                                    onClick={() => toggleMemoExpand(item.id)}
                                                    sx={{ p: 0.25, color: currentMemo ? textColors(theme).muted : textColors(theme).disabled }}
                                                  >
                                                    <EditNoteIcon fontSize="small" />
                                                  </IconButton>
                                                )}
                                              </>
                                            )}
                                          </Box>
                                        </TableCell>

                                        {/* 削除ボタン */}
                                        {!isReadOnly && (
                                          <TableCell sx={{ textAlign: 'center' }}>
                                            <Tooltip title="削除">
                                              <IconButton
                                                onClick={() => handleDeleteQuestion(categoryIndex, itemIndex)}
                                                size="small"
                                                sx={{
                                                  p: 0.25,
                                                  color: textColors(theme).disabled,
                                                  '&:hover': { color: '#ef4444' }
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    </React.Fragment>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Box sx={{ p: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                textAlign: 'center',
                                fontStyle: 'italic',
                                color: 'text.secondary'
                              }}
                            >
                              この評価項目にはまだ評価内容がありません
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </AnimatePresence>
                </Card>
              );
            })
          )}
        </Box>
      </Paper>

      {/* 成功・エラーメッセージ表示用Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar(prev => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: shadow(theme).lg
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)',
            color: 'white',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WarningIcon sx={{ fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            評価項目の削除確認
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
              以下の評価項目を削除してもよろしいですか？
            </Typography>

            <Box
              sx={{
                p: 2,
                bgcolor: alpha('#f44336', 0.05),
                borderRadius: 2,
                border: `1px solid ${alpha('#f44336', 0.2)}`
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                評価項目名
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336', mb: 1.5 }}>
                {deleteDialog.categoryName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                この評価項目内のすべての質問（{deleteDialog.itemCount}件）と回答データも削除されます。
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2,
                bgcolor: alpha('#ff9800', 0.05),
                borderRadius: 2,
                border: `1px dashed ${alpha('#ff9800', 0.3)}`,
                display: 'flex',
                alignItems: 'start',
                gap: 1.5
              }}
            >
              <WarningIcon sx={{ color: '#ff9800', fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', mb: 0.5 }}>
                  注意事項
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  この操作は取り消すことができません。削除されたデータは復元できませんのでご注意ください。
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: alpha('#000', 0.23),
              color: 'text.primary',
              '&:hover': {
                borderColor: alpha('#000', 0.4),
                bgcolor: alpha('#000', 0.04)
              }
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: '0 4px 12px rgba(244,67,54,0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(244,67,54,0.4)'
              }
            }}
          >
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

export default InterviewCheckSheet; 